/**
 * Pre-first-turn resume probe for the Claude Agent SDK driver.
 *
 * A tier-1 native resume only hands its session id to the SDK inside the first
 * real turn, so the `jsonl_lost` outcome ("No conversation found") is invisible
 * until a turn is consumed. The runner shares the container filesystem with the
 * SDK, so a cheap `stat` of the on-disk JSONL transcript is an authoritative,
 * side-effect-free signal of whether the resume will find its history — no
 * probe query, no spurious model turn.
 *
 * @module
 */

import { homedir } from "node:os";
import { join } from "node:path";
import { withFsDeadline } from "@skaile/workspaces/core";
import { locateTranscriptPath } from "./scrub-transcript.js";

/**
 * Overall wall-clock budget for the probe. Deliberately a small fraction of the
 * platform's `session_init_ack` timeout (`DEFAULT_INIT_ACK_TIMEOUT_MS`, 15s):
 * the probe runs synchronously on the pre-ack path, so a wedged
 * `CLAUDE_CONFIG_DIR` mount must never push the ack past its deadline.
 */
const RESUME_PROBE_TIMEOUT_MS = 2_000;

/**
 * Outcome of {@link probeResumeTranscript}: whether the SDK transcript for a
 * requested tier-1 resume exists on disk.
 *
 * @category Resume probe
 */
export type ResumeProbeOutcome = "transcript_found" | "jsonl_lost";

/**
 * Resolve the Claude Code config directory — the parent of `projects/` — from
 * an explicit env map, the process environment, or the `~/.claude` default.
 *
 * The runner's probe and the claude-sdk driver share this one resolver, so they
 * agree as long as they see the same `CLAUDE_CONFIG_DIR`. Today they do:
 * `CLAUDE_CONFIG_DIR` is container-scoped and neither side threads a per-session
 * override through `config.env`, so both fall through to `process.env`. If a
 * future change ever set the driver's `config.env.CLAUDE_CONFIG_DIR` to a
 * divergent path, the reactive `resume_failed { jsonl_lost }` turn remains the
 * safety net.
 *
 * @category Resume probe
 */
export function resolveClaudeConfigDir(env?: Record<string, string | undefined>): string {
  return env?.CLAUDE_CONFIG_DIR || process.env.CLAUDE_CONFIG_DIR || join(homedir(), ".claude");
}

/**
 * Probe whether the SDK JSONL transcript for `sessionId` exists on disk.
 *
 * Non-null locator result → `transcript_found`; null → `jsonl_lost`;
 * `undefined` → outcome unknown (the probe exceeded its budget or errored). The
 * caller leaves the ack field ABSENT on `undefined` so the reactive
 * `resume_failed { jsonl_lost }` turn stays the safety net — a timed-out probe
 * must never masquerade as a genuine `jsonl_lost` and force a needless downgrade.
 *
 * `opts.timeoutMs` overrides {@link RESUME_PROBE_TIMEOUT_MS}; `opts.locate`
 * overrides the transcript locator (used by tests / alternate stores).
 *
 * @category Resume probe
 */
export async function probeResumeTranscript(
  sessionId: string,
  opts?: {
    env?: Record<string, string | undefined>;
    timeoutMs?: number;
    locate?: (configDir: string, sessionId: string) => Promise<string | null>;
  },
): Promise<ResumeProbeOutcome | undefined> {
  const configDir = resolveClaudeConfigDir(opts?.env);
  const timeoutMs = opts?.timeoutMs ?? RESUME_PROBE_TIMEOUT_MS;
  const locate = opts?.locate ?? locateTranscriptPath;
  try {
    // Bound the whole probe on wall-clock and THROW on expiry: the inner per-op
    // deadline swallows a timeout into a null path (a false `jsonl_lost`), so a
    // wedged mount must yield "unknown" here rather than a spurious downgrade.
    const filePath = await withFsDeadline(
      `resume-probe ${sessionId}`,
      () => locate(configDir, sessionId),
      timeoutMs,
    );
    return filePath ? "transcript_found" : "jsonl_lost";
  } catch {
    return undefined;
  }
}
